import { useCallback, useMemo, useRef, useState } from 'react'
import { useApolloClient } from 'apollo-client'
import { GraphQLError, processError, ServerError, UserError } from 'helpers'
import logger from 'logger'
import type { InitialSubscriptionPlan } from 'typings/graphql'

import modifyInquire from './modifyInquire'
import inquireQuery from './graph/inquire.graphql'


type UseInquireProviderProps = {
  purchaseLevel?: PaymentModule.PurchaseLevel
  shippingForm: PaymentModule.ShippingForm
  billingForm: PaymentModule.ShippingForm
  initialSubscriptionPlan?: InitialSubscriptionPlan
}

const getAddress = (values: PaymentModule.ShippingFormFields) => {
  const { country, postalCode, region, city, street1, street2 } = values

  const isRequiredFieldsFilled = Boolean(country && postalCode && region && city && street1)

  if (!isRequiredFieldsFilled) {
    if (!country) {
      return null
    }

    return {
      country,
    }
  }

  // TODO validate postal code if needed - added on 2021-09-03 by maddoger

  return {
    country,
    postalCode,
    region,
    city,
    street1,
    street2,
  }
}

const useInquireProvider = (props: UseInquireProviderProps): PaymentModule.InquireContext => {
  const { purchaseLevel, shippingForm, billingForm, initialSubscriptionPlan } = props

  const apolloClient = useApolloClient()
  const [ state, setState ] = useState<{ orderData: PaymentModule.InquireData, isFetching: boolean }>({ orderData: null, isFetching: false })
  const queryPromiseRef = useRef<any>(null)
  const queryPromiseCounterRef = useRef(0)
  const isFirstQueryRef = useRef(true)
  const isGiftsOnlyRef = useRef(false)
  const initialSubscriptionPlanRef = useRef<InitialSubscriptionPlan | undefined>(initialSubscriptionPlan)
  initialSubscriptionPlanRef.current = initialSubscriptionPlan

  const submit: PaymentModule.InquireContext['submit'] = useCallback(async (options) => {
    const currentCounter = ++queryPromiseCounterRef.current

    try {
      setState((state) => ({
        ...state,
        isFetching: true,
      }))

      let inquireInitialSubscriptionPlan

      // TODO remove it when cart will be implemented - added on 2021-10-01 by maddoger
      // we should add subscription if user is on the subscribe page or if it's a first inquire for checkout page
      if (initialSubscriptionPlanRef.current && (purchaseLevel === 'SUBSCRIPTION_ONLY' || purchaseLevel === 'ALL' && isFirstQueryRef.current)) {
        inquireInitialSubscriptionPlan = initialSubscriptionPlanRef.current
        isFirstQueryRef.current = false
      }

      if (options?.initialSubscriptionPlan) {
        inquireInitialSubscriptionPlan = options?.initialSubscriptionPlan
      }

      const createQueryPromise = async () => {
        // for digital products we use billing address instead of the shipping,
        // so we need to send billing to inquire too for tax calculation
        const address = getAddress(
          isGiftsOnlyRef.current
            ? billingForm.getValues()
            : shippingForm.getValues()
        )

        logger.info(`[Inquire] send inquire for ${purchaseLevel} ${address ? 'with address' : ''}`)

        const result = await apolloClient.query({
          fetchPolicy: 'no-cache',
          query: inquireQuery,
          variables: {
            input: {
              purchaseLevel,
              address,
              initialSubscriptionPlan: inquireInitialSubscriptionPlan,
            },
          },
        })

        if (result.errors) {
          throw new GraphQLError(result.errors)
        }

        if (!result.data.purchaseInquiry) {
          throw new ServerError(result.data, 'purchaseInquiry is undefined')
        }

        const { data, error } = result.data.purchaseInquiry

        // logic error
        if (error) {
          if ('cartErrorCode' in error) {
            // Do not throw error for empty cart, we will redirect user according to payment page logic
            if (error.cartErrorCode === 'CART_NOT_FOUND') {
              return modifyInquire(null, purchaseLevel)
            }

            // Do not throw error for empty cart with limited drops,
            // we will show payment page with disabled button and user can reactivate limited drop
            if (error.cartErrorCode === 'CART_IS_EMPTY') {
              return modifyInquire(data, purchaseLevel, true)
            }
          }

          throw new UserError(error)
        }

        const orderData = modifyInquire(data, purchaseLevel)

        logger.info('[Inquire] result', {
          extra: {
            orderData,
            error,
          },
        })

        // we should re-fetch the cart if user added subscription by inquire
        if (inquireInitialSubscriptionPlan) {
          apolloClient.cache.evict({ id: 'UserData:{}', fieldName: 'cart' })
        }

        if (orderData.isGiftsOnly !== isGiftsOnlyRef.current) {
          isGiftsOnlyRef.current = orderData.isGiftsOnly
          // refetch with the new address
          return createQueryPromise()
        }

        return orderData
      }

      // wrap the function to keep
      const queryPromise = createQueryPromise()

      // if we have promise, we need to provide new data to it
      if (queryPromiseRef.current) {
        queryPromiseRef.current.then(() => queryPromise)
      }

      // and override value for new waiters
      queryPromiseRef.current = queryPromise

      const orderData = await queryPromise

      // update state only if current query is current
      // otherwise do nothing
      if (currentCounter === queryPromiseCounterRef.current) {
        setState({
          orderData,
          isFetching: false,
        })
      }
    }
    catch (error) {
      setState((state) => ({
        ...state,
        isFetching: false,
      }))

      processError(error, '[Inquire] error')
    }
  }, [ apolloClient, shippingForm, billingForm, purchaseLevel ])

  const waitForData = useCallback(async () => {
    return await queryPromiseRef.current
  }, [])

  const setFetching = useCallback((isFetching: boolean) => {
    setState((state) => ({
      ...state,
      isFetching,
    }))
  }, [])

  const { orderData, isFetching } = state

  return useMemo<PaymentModule.InquireContext>(() => {
    return {
      orderData,
      submit,
      waitForData,
      isFetching,
      setFetching,
    }
  }, [ submit, setFetching, orderData, isFetching, waitForData ])
}


export default useInquireProvider
